import React from 'react';
import { Table, Tag, Typography } from 'antd';
import { TICKET_CLASS_IDS, TRIAGE_ACTIONS } from '../../types/triage';
import { classTagMap, classTooltipMap } from '../triageMaps';

// Single source of truth for the operator-facing 9-class taxonomy. Used by
// both the Help page glossary and the onboarding tour so the class names,
// action-bar numbers, and "when to pick" guidance never drift apart.
//
// The action-bar number comes from TRIAGE_ACTIONS.shortcutKey (the same keys
// 1-8 the keyboard shortcuts fire). OFFLINE_UNKNOWN has no button by design —
// it is the sink the chain falls back to and the agent is forbidden from
// splitting on its own.
export interface ClassGlossaryRow {
  id: number;
  /** Action-bar / keyboard number, or null for the no-button sink class. */
  button: string | null;
  whenToPick: string;
}

const WHEN_TO_PICK: Record<number, string> = {
  [TICKET_CLASS_IDS.BATTERY_DRAINED]:
    'Voltage collapsed or the battery snapshot says "change batteries" on an otherwise-healthy node. Natural cell depletion — not a hardware fault.',
  [TICKET_CLASS_IDS.OFFLINE_UNKNOWN]:
    'The honest "we don\'t know" sink. Leave an issue here only when no signal explains the outage. The agent cannot move an issue off this class on its own — corroborating exemplars are required.',
  [TICKET_CLASS_IDS.HARDWARE_FAULT]:
    'Confirmed hardware failure with a healthy battery. Action: replace the node.',
  [TICKET_CLASS_IDS.HARDWARE_FAULT_EOL]:
    'Whole node is past its lifecycle (old age + repeat offences). Action: replace the node.',
  [TICKET_CLASS_IDS.TAMPERING]:
    'Physical removal or damage — "removed" in the battery action, or a sudden dropout on a node with healthy recent voltage.',
  [TICKET_CLASS_IDS.FIRMWARE_ISSUE]:
    'Firmware bug or a failed OTA correlated with the outage window. Action: update firmware.',
  [TICKET_CLASS_IDS.OUT_OF_RANGE]:
    'Inferred position/signal problem — the node was recently re-mapped (moved) or had a weak signal (< −85 dBm) before dropping. NOT a confirmed measurement: weak signal can also be the gateway, interference, or a failing radio. Action: verify the node\'s position on-site.',
  [TICKET_CLASS_IDS.ON_SITE_INVESTIGATION]:
    'Cause cannot be determined remotely — a physical site visit is needed to diagnose.',
  [TICKET_CLASS_IDS.FALSE_ALERT]:
    'Sensor noise or a transient blip — no real fleet problem.',
};

// Button number per class, derived from TRIAGE_ACTIONS so it tracks the real
// action bar. OFFLINE_UNKNOWN is absent there (the sink) → null.
const BUTTON_BY_CLASS: Record<number, string> = TRIAGE_ACTIONS.reduce(
  (acc, a) => {
    if (a.ticketClassId != null) acc[a.ticketClassId] = a.shortcutKey;
    return acc;
  },
  {} as Record<number, string>,
);

// Canonical display order: the sink first, then the 8 actionable classes in
// button order.
export const CLASS_GLOSSARY: ClassGlossaryRow[] = [
  TICKET_CLASS_IDS.OFFLINE_UNKNOWN,
  TICKET_CLASS_IDS.BATTERY_DRAINED,
  TICKET_CLASS_IDS.HARDWARE_FAULT,
  TICKET_CLASS_IDS.HARDWARE_FAULT_EOL,
  TICKET_CLASS_IDS.TAMPERING,
  TICKET_CLASS_IDS.FIRMWARE_ISSUE,
  TICKET_CLASS_IDS.OUT_OF_RANGE,
  TICKET_CLASS_IDS.ON_SITE_INVESTIGATION,
  TICKET_CLASS_IDS.FALSE_ALERT,
].map((id) => ({
  id,
  button: BUTTON_BY_CLASS[id] ?? null,
  whenToPick: WHEN_TO_PICK[id],
}));

const ClassGlossary: React.FC = () => (
  <Table<ClassGlossaryRow>
    dataSource={CLASS_GLOSSARY}
    rowKey="id"
    pagination={false}
    size="small"
    columns={[
      {
        title: 'ID',
        dataIndex: 'id',
        width: 56,
        render: (id: number) => <Typography.Text code>{id}</Typography.Text>,
      },
      {
        title: 'Class',
        dataIndex: 'id',
        width: 200,
        render: (id: number) => {
          const tag = classTagMap[id];
          return (
            <span>
              <Tag color={tag?.color}>{tag?.label}</Tag>{' '}
              {classTooltipMap[id]}
            </span>
          );
        },
      },
      {
        title: 'Button',
        dataIndex: 'button',
        width: 72,
        render: (b: string | null) =>
          b ? (
            <kbd
              style={{
                background: '#f5f5f5',
                border: '1px solid #d9d9d9',
                borderRadius: 4,
                padding: '1px 7px',
                fontFamily: 'monospace',
                fontWeight: 600,
              }}
            >
              {b}
            </kbd>
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          ),
      },
      {
        title: 'When to pick',
        dataIndex: 'whenToPick',
      },
    ]}
  />
);

export default ClassGlossary;
